// src/App.js

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import MultiCloudComplianceDashboard from './pages/Cloud/MultiCloudComplianceDashboard';
import SystemAuthExpiredModal from './pages/Company/CompanyBaseInfo/SystemAuthExpiredModal';
import AppMenu from './components/portal/AppMenu';
import AppHeader from './components/portal/AppHeader';
import { AndaProvider, AndaChatButton, AndaChatPanel, useAnda } from './components/anda';
import './App.css';
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "./store/slices/userSlice";
import { fetchBusinessMembers } from './store/slices/businessSlice';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { checkTokenOnInit, setupTokenFocusCheck } from './js/Base/tokenManager';

import ProtectedRoute from './routes/ProtectedRoute';
import { publicRoutes, commonRoutes, managerRoutes } from './routes/index.js';

/* =========================
  Layout Wrapper
========================= */
function MainContentWrapper({ menuOpen, children }) {
  const { chatOpen, chatWidth, resizing } = useAnda();
  return (
    <main
      className={`main-content ${menuOpen ? 'open' : ''} ${chatOpen ? 'anda-open' : ''} ${resizing ? 'anda-resizing' : ''}`}
      style={chatOpen ? { marginRight: chatWidth } : undefined}
    >
      {children}
    </main>
  );
}

/* =========================
  App Content
========================= */
function AppContent() {
  const location = useLocation();
  const isLoginPage =
    location.pathname === '/' || location.pathname === '/forget-password' || location.pathname.startsWith('/resetpwd/');

  const [menuOpen, setMenuOpen] = useState(false);
  const handleStateChange = (state) => setMenuOpen(state.isOpen);
  const dispatch = useDispatch();
  /* =========================
    Menu Reset（登入頁關閉）
  ========================= */
  useEffect(() => {
    if (isLoginPage) {
      setMenuOpen(false);
    }
  }, [isLoginPage]);

  /* =========================
    Fetch Member Role
  ========================= */
  useEffect(() => {
    if (!isLoginPage) {
      dispatch(fetchBusinessMembers());
    }
  }, [isLoginPage, location.pathname]);

  const isBusinessManager = useSelector(state => state.businessMembers.isBusinessManager);
  const isEdcAdmin = useSelector(state => state.businessMembers.isEdcAdmin);
  const isAdmin = useSelector(state => state.user.isAdmin);

  /* =========================
    Init User
  ========================= */
  useEffect(() => {
    const userName = localStorage.getItem("user_email");
    dispatch(setUser({ email: userName }));
  }, [dispatch, isLoginPage]);

  /* =========================
    License 控制
  ========================= */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalTimerRef = useRef(null);
  const handleAddModalClose = () => setIsModalOpen(false);

  const license = useSelector((state) => state.license.data);
  useEffect(() => {
    const checkLicenseExpired = () => {
      if (!license) return false;

      const isExpired = new Date(license.expiry_date) <= new Date();
      return isExpired;
    };

    const handleExpired = () => {
      setIsModalOpen(true);
      forceShowModalLoop(modalTimerRef);
    };

    // 登入後頁面都生效
    if (!isLoginPage && checkLicenseExpired()) {
      handleExpired();
    }

    // 如果過期就跳 modal
    if (!isLoginPage && license) {
      if (checkLicenseExpired()) {
        handleExpired();
      }
    }

    return () => {
      if (modalTimerRef.current) clearInterval(modalTimerRef.current);
    };
  }, [license, isLoginPage]);


  const forceShowModalLoop = () => {
    if (modalTimerRef.current) clearInterval(modalTimerRef.current);
    modalTimerRef.current = setInterval(() => {
      setIsModalOpen(true);
    }, 3000);
  };
  const handleLicenseUpdated = async (status) => {
    if (status) {
      // 成功更新：停止強制跳 modal
      if (modalTimerRef.current) clearInterval(modalTimerRef.current);
      setIsModalOpen(false);
    }
  };

  /* =========================
    Login Routes（簡潔）
  ========================= */
  if (isLoginPage) {
    return (
      <>
        <Routes>
          {publicRoutes.map(r => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
        </Routes>
      </>
    );
  }


  // 角色
  const userRole = isAdmin
  ? "admin"
  : isEdcAdmin
  ? "edcAdmin"
  : isBusinessManager
  ? "manager"
  : "user";

  /* =========================
    Main App（含 Anda）
  ========================= */
  return (
    <AndaProvider>
      <AppHeader menuOpen={menuOpen} handleStateChange={(state) => setMenuOpen(state.isOpen)} />
      <AndaChatButton />
      <AndaChatPanel />

      <div className="AppLoginHeader">
        <MainContentWrapper menuOpen={menuOpen}>

          {/* License Modal */}
          <SystemAuthExpiredModal
            isModalOpen={isModalOpen}
            toggle={() => setIsModalOpen(false)}
            onUpdated={handleLicenseUpdated}
            handleClose={handleAddModalClose}
          />

          <Routes>
            {[...commonRoutes, ...managerRoutes].map(r => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  <ProtectedRoute roles={r.roles} userRole={userRole}>
                    {r.element}
                  </ProtectedRoute>
                }
              />
            ))}

            <Route path="*" element={<MultiCloudComplianceDashboard />} />
          </Routes>
        </MainContentWrapper>
      </div>
    </AndaProvider>
  );
}

export default function App() {
  useEffect(() => {
    checkTokenOnInit();
    setupTokenFocusCheck();
  }, []);

  return (
    <Router>
      <AppContent />
      <ToastContainer position="top-center" style={{ zIndex: 20000 }} />
    </Router>
  );
}
